import type * as THREE from 'three'
import { Sphere } from 'three'
import type { NodeLoader } from '@/plugins/pointCloud/loaders/OctreeLoader'
import type { PointAttributes } from '@/plugins/pointCloud/loaders/PointAttributes'
import type { PotreeConfig } from '@/plugins/pointCloud/loaders/types'

const NODE_LOAD_RETRY_DELAYS_MS = [1000, 3000, 10000, 30000]
const NODE_LOAD_MAX_RETRY_COUNT = 5

/** 点云节点可释放资源的生命周期状态。 */
export type NodeResourceState = 'unloaded' | 'loading' | 'decoded' | 'resident' | 'disposed'

/** 节点当前持有资源的估算权重。 */
export interface NodeResourceMetrics {
  points: number
  estimatedCpuBytes: number
  estimatedGpuBytes: number
  estimatedTotalBytes: number
}

/** 单次节点释放的实际记录。 */
export interface NodeReleaseRecord extends NodeResourceMetrics {
  released: boolean
  previousState: NodeResourceState
}

/** 节点资源状态变更监听器。 */
export type NodeResourceStateListener = (
  node: OctreeGeometryNode,
  previousState: NodeResourceState,
  state: NodeResourceState
) => void

const EMPTY_RESOURCE_METRICS: NodeResourceMetrics = {
  points: 0,
  estimatedCpuBytes: 0,
  estimatedGpuBytes: 0,
  estimatedTotalBytes: 0,
}

function getArrayByteLength(value: unknown): number {
  return ArrayBuffer.isView(value) ? value.byteLength : 0
}

/** 估算 CPU 持有量和渲染后预期的 VBO 上界，不代表 renderer 已实际创建 GPU 资源。 */
function estimateGeometryBytes(geometry: THREE.BufferGeometry | null | undefined): {
  cpu: number
  gpu: number
} {
  if (!geometry) {
    return { cpu: 0, gpu: 0 }
  }

  const ownedBuffers = new Set<ArrayBufferLike>()
  let cpu = 0
  let gpu = 0
  const countAttribute = (attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) => {
    const array = attribute.array
    if (ArrayBuffer.isView(array) && !ownedBuffers.has(array.buffer)) {
      ownedBuffers.add(array.buffer)
      cpu += array.byteLength
      gpu += array.byteLength
    }

    const preciseBuffer = (
      attribute as THREE.BufferAttribute & {
        potree?: { preciseBuffer?: unknown }
      }
    ).potree?.preciseBuffer
    const preciseBytes = getArrayByteLength(preciseBuffer)
    if (preciseBytes > 0) {
      const preciseArray = preciseBuffer as ArrayBufferView
      if (!ownedBuffers.has(preciseArray.buffer)) {
        ownedBuffers.add(preciseArray.buffer)
        cpu += preciseBytes
      }
    }
  }

  for (const attribute of Object.values(geometry.attributes)) {
    countAttribute(attribute)
  }
  if (geometry.index) {
    countAttribute(geometry.index)
  }

  return { cpu, gpu }
}

function nowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now()
}

/**
 * 八叉树几何对象，聚合根节点、包围盒、点属性等元数据。
 *
 * @description 八叉树几何对象
 * @link potree-core/src/workers/PotreeV2DecoderWorker.ts
 */
export class OctreeGeometry {
  /** 八叉树数据 URL */
  url: string | null = null

  /** 点间距 */
  spacing: number = 0

  /** 包围盒 */
  boundingBox: THREE.Box3 | null = null

  /** 根节点 */
  root: OctreeGeometryNode | null = null

  /** Potree metadata 声明的整个点云总点数；未知时为 0。 */
  totalPoints: number = 0

  /** 点属性 */
  pointAttributes: PointAttributes | null = null

  /** 加载器 */
  loader: NodeLoader | null = null

  /** 是否已释放，迟到节点加载结果必须丢弃 */
  disposed = false

  /** 节点资源状态监听器，由驻留管理器在资产存活期间订阅。 */
  private readonly resourceStateListeners = new Set<NodeResourceStateListener>()

  /** 资产生命周期取消信号，用于后续 LOD 懒加载 */
  loadSignal?: AbortSignal

  /** 缩放比例 */
  scale?: number

  /** 偏移量 */
  offset?: THREE.Vector3

  /** 投影坐标系 */
  projection?: string

  /** 紧密包围盒 */
  tightBoundingBox?: THREE.Box3

  /** 包围球 */
  boundingSphere?: THREE.Sphere

  /** 紧密包围球 */
  tightBoundingSphere?: THREE.Sphere

  /** 最小紧密包围盒 */
  mini_tightBoundingBox?: THREE.Box3

  /** 备用投影坐标系 */
  fallbackProjection?: string

  /** 订阅节点资源状态变化。 */
  addResourceStateListener(listener: NodeResourceStateListener): () => void {
    this.resourceStateListeners.add(listener)
    return () => this.resourceStateListeners.delete(listener)
  }

  /** 向驻留层广播节点资源状态变化。 */
  notifyResourceStateChanged(
    node: OctreeGeometryNode,
    previousState: NodeResourceState,
    state: NodeResourceState
  ): void {
    for (const listener of this.resourceStateListeners) {
      listener(node, previousState, state)
    }
  }

  /** 深度释放八叉树几何节点与加载中的请求。 */
  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true

    if (this.root) {
      const stack: OctreeGeometryNode[] = [this.root]
      while (stack.length > 0) {
        const node = stack.pop()!
        for (const child of node.getChildren()) {
          stack.push(child)
        }
        node.dispose()
      }
    }
    this.resourceStateListeners.clear()
  }
}

/**
 * 八叉树几何节点，承载一个八叉树结点的元信息与子节点引用。
 *
 * @description 八叉树几何节点，表示八叉树中的一个节点
 */
export class OctreeGeometryNode {
  /** 节点唯一 ID */
  id: number

  /** 节点名称 */
  name: string

  /** 节点索引（从名称最后一位解析） */
  index: number

  /** 所属八叉树几何对象 */
  octreeGeometry: OctreeGeometry

  /** 节点包围盒 */
  boundingBox: THREE.Box3

  /** 节点包围球 */
  boundingSphere: THREE.Sphere

  /** 子节点集合（索引 0-7 对应八叉树的 8 个子节点） */
  children: Record<number, OctreeGeometryNode | undefined> = {}

  /** 节点包含的点数量 */
  numPoints: number = 0

  /** 节点层级 */
  level: number | null = null

  /** 一次性释放处理函数列表 */
  oneTimeDisposeHandlers: Array<() => void> = []

  /** 几何体数据 */
  geometry: any = null

  /** 父节点 */
  parent?: OctreeGeometryNode | null

  /** 节点最近一次加载是否失败，失败后按 retryAfter 退避重试 */
  loadFailed = false

  /** 连续加载失败次数，用于计算退避时间 */
  loadErrorCount = 0

  /** 下一次允许重试的时间戳，单位 ms，基于 performance.now() */
  retryAfter = 0

  /** 最近一次加载失败原因，供诊断日志使用 */
  lastLoadError?: string

  /** 连续失败超过上限后置位，避免坏节点永久周期性重试 */
  loadPermanentlyFailed = false

  /** 节点类型 */
  nodeType?: number

  /** 字节偏移 */
  byteOffset?: bigint

  /** 字节大小 */
  byteSize?: bigint

  /** 密度 */
  density?: number

  /** 层级数据字节偏移 */
  hierarchyByteOffset?: bigint

  /** 层级数据字节大小 */
  hierarchyByteSize?: bigint

  /** 点间距 */
  spacing?: number

  /** 是否有子节点 */
  hasChildren?: boolean

  /** 当前唯一资源生命周期状态。 */
  private _resourceState: NodeResourceState = 'unloaded'

  /** 当前加载请求代次；unload/dispose 会递增以拒绝晚到结果。 */
  private _loadGeneration = 0

  /** 是否为仅承载拓扑、不拥有点数据的结构节点。 */
  private _structural = false

  /** 静态 ID 计数器 */
  static IDCount = 0

  /** Potree 全局配置对象 */
  Potree: PotreeConfig

  /**
   * 构造八叉树几何节点
   * @param name - 节点名称
   * @param octreeGeometry - 所属八叉树几何对象
   * @param boundingBox - 节点包围盒
   * @param Potree - Potree 全局配置对象
   */
  constructor(
    name: string,
    octreeGeometry: OctreeGeometry,
    boundingBox: THREE.Box3,
    Potree: PotreeConfig
  ) {
    this.id = OctreeGeometryNode.IDCount++
    this.name = name
    this.index = parseInt(name.charAt(name.length - 1), 10)
    this.octreeGeometry = octreeGeometry
    this.boundingBox = boundingBox
    this.boundingSphere = boundingBox.getBoundingSphere(new Sphere())
    this.Potree = Potree
  }

  /** 当前节点资源状态的只读快照。 */
  get resourceState(): NodeResourceState {
    return this._resourceState
  }

  /** 当前载荷代次；unload、dispose、取消或新一代加载都会递增。 */
  get resourceGeneration(): number {
    return this._loadGeneration
  }

  /** 兼容旧读取：decoded/resident 均表示 CPU 几何已加载。 */
  get loaded(): boolean {
    return this._resourceState === 'decoded' || this._resourceState === 'resident'
  }

  /** 兼容旧读取：仅 loading 状态返回 true。 */
  get loading(): boolean {
    return this._resourceState === 'loading'
  }

  /** 兼容旧读取：disposed 为不可逆终态。 */
  get disposed(): boolean {
    return this._resourceState === 'disposed'
  }

  /** 当前节点是否只参与拓扑遍历。 */
  get structural(): boolean {
    return this._structural
  }

  /**
   * 判断是否为几何节点
   * @returns 始终返回 true
   */
  isGeometryNode(): boolean {
    return true
  }

  /**
   * 获取节点层级
   * @returns 节点层级，如果未设置则返回 null
   */
  getLevel(): number | null {
    return this.level
  }

  /**
   * 判断是否为树节点
   * @returns 始终返回 false
   */
  isTreeNode(): boolean {
    return false
  }

  /**
   * 判断节点是否已加载
   * @returns 加载状态
   */
  isLoaded(): boolean | undefined {
    return this.loaded
  }

  /**
   * 获取节点包围球
   * @returns 包围球对象
   */
  getBoundingSphere(): THREE.Sphere {
    return this.boundingSphere
  }

  /**
   * 获取所有子节点
   * @returns 子节点数组
   */
  getChildren(): OctreeGeometryNode[] {
    const children: OctreeGeometryNode[] = []

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]!)
      }
    }

    return children
  }

  /**
   * 获取节点包围盒
   * @returns 包围盒对象
   */
  getBoundingBox(): THREE.Box3 {
    return this.boundingBox
  }

  /**
   * 加载节点数据
   * @description 当正在加载的节点数量未超过最大限制时，通过加载器加载节点数据
   * @returns true 表示本次真正发起了加载，false 表示被状态/退避/预算拦截
   */
  load(signal: AbortSignal | undefined = this.octreeGeometry.loadSignal): boolean {
    if (!this.canStartLoad(signal)) {
      return false
    }

    void this.octreeGeometry.loader?.load(this, signal)
    return true
  }

  /** 判断节点当前是否允许发起加载，不产生副作用。 */
  canStartLoad(signal: AbortSignal | undefined = this.octreeGeometry.loadSignal): boolean {
    if (this.loaded || this.loading || this._structural) {
      return false
    }
    if (this.disposed || this.octreeGeometry.disposed || signal?.aborted) {
      return false
    }
    if (this.loadPermanentlyFailed) {
      return false
    }
    if (this.loadFailed && nowMs() < this.retryAfter) {
      return false
    }
    if (this.Potree.numNodesLoading >= this.Potree.maxNodesLoading) {
      return false
    }

    return !!this.octreeGeometry.loader
  }

  /** 开始一次受 token 保护的加载，返回请求 token；不允许加载时返回 null。 */
  beginLoad(signal: AbortSignal | undefined = this.octreeGeometry.loadSignal): number | null {
    if (!this.canStartLoad(signal)) {
      return null
    }

    this._loadGeneration += 1
    this._setResourceState('loading')
    return this._loadGeneration
  }

  /** 判断异步结果是否仍属于当前加载请求。 */
  isLoadTokenCurrent(token: number): boolean {
    return (
      token === this._loadGeneration &&
      this._resourceState === 'loading' &&
      !this.octreeGeometry.disposed
    )
  }

  /** 提交解码结果；token 失效时返回 false，调用方负责释放传入 geometry。 */
  completeDecoded(token: number, geometry: THREE.BufferGeometry, density?: number): boolean {
    if (!this.isLoadTokenCurrent(token)) {
      return false
    }

    this.geometry = geometry
    this.density = density
    this.clearLoadFailure()
    this._setResourceState('decoded')
    return true
  }

  /** 将已解码节点标记为可渲染驻留。 */
  markResident(): boolean {
    if (this._resourceState === 'resident') {
      return true
    }
    if (this._resourceState !== 'decoded' || !this.geometry) {
      return false
    }
    this._setResourceState('resident')
    return true
  }

  /** 将零字节节点标记为纯结构节点，不创建任何载荷。 */
  markStructural(): void {
    if (this.disposed) {
      return
    }
    this._structural = true
    this._releaseLoadedGeometry()
    this.clearLoadFailure()
    if (this._resourceState !== 'loading') {
      this._loadGeneration += 1
      this._setResourceState('unloaded')
    }
  }

  /** 完成结构节点的层级加载，不创建 CPU/GPU 载荷。 */
  completeStructural(token: number): boolean {
    if (!this.isLoadTokenCurrent(token)) {
      return false
    }
    this._structural = true
    this._loadGeneration += 1
    this._releaseLoadedGeometry()
    this.clearLoadFailure()
    this._setResourceState('unloaded')
    return true
  }

  /** 取消仍有效的加载 token。 */
  cancelLoad(token?: number): void {
    if (token !== undefined && token !== this._loadGeneration) {
      return
    }
    if (this._resourceState !== 'loading') {
      return
    }
    this._loadGeneration += 1
    this._setResourceState('unloaded')
  }

  /** 标记节点加载失败，并设置指数退避，避免同一坏节点每帧重试。 */
  markLoadFailed(reason?: string, token?: number): void {
    if (token !== undefined && token !== this._loadGeneration) {
      return
    }
    if (this.disposed) {
      return
    }
    this._loadGeneration += 1
    this._setResourceState('unloaded')
    this.loadFailed = true
    this.loadErrorCount += 1
    this.lastLoadError = reason

    if (this.loadErrorCount >= NODE_LOAD_MAX_RETRY_COUNT) {
      this.loadPermanentlyFailed = true
      this.retryAfter = Number.POSITIVE_INFINITY
      return
    }

    const delay =
      NODE_LOAD_RETRY_DELAYS_MS[
        Math.min(this.loadErrorCount - 1, NODE_LOAD_RETRY_DELAYS_MS.length - 1)
      ]
    this.retryAfter = nowMs() + delay
  }

  /** 节点加载成功后清理失败状态。 */
  clearLoadFailure(): void {
    this.loadFailed = false
    this.loadErrorCount = 0
    this.retryAfter = 0
    this.lastLoadError = undefined
    this.loadPermanentlyFailed = false
  }

  /**
   * 获取节点包含的点数量
   * @returns 点数量
   */
  getNumPoints(): number {
    return this.numPoints
  }

  /** 获取当前资源权重；GPU 分项是预期 VBO 上界，读取不改变状态或最近使用顺序。 */
  getResourceMetrics(): NodeResourceMetrics {
    if (!this.loaded || !this.geometry) {
      return { ...EMPTY_RESOURCE_METRICS }
    }

    const estimated = estimateGeometryBytes(this.geometry)
    const estimatedGpuBytes = this._resourceState === 'resident' ? estimated.gpu : 0
    return {
      points: this.numPoints,
      estimatedCpuBytes: estimated.cpu,
      estimatedGpuBytes,
      estimatedTotalBytes: estimated.cpu + estimatedGpuBytes,
    }
  }

  /**
   * 释放节点资源
   * @description 释放几何体资源并执行一次性释放处理函数
   */
  dispose(): NodeReleaseRecord {
    return this._release('disposed')
  }

  /**
   * 卸载已加载的节点数据，但保留节点后续重新加载能力。
   *
   * LRU 缓存淘汰使用此方法；永久销毁资源才使用 dispose()。
   */
  unload(): NodeReleaseRecord {
    return this._release('unloaded')
  }

  private _release(
    finalState: Extract<NodeResourceState, 'unloaded' | 'disposed'>
  ): NodeReleaseRecord {
    if (this.disposed) {
      return this._emptyReleaseRecord('disposed')
    }
    const previousState = this._resourceState
    const metrics = this.getResourceMetrics()
    const released = this.geometry !== null
    this._loadGeneration += 1
    this.loadFailed = false
    this.retryAfter = 0
    this.loadPermanentlyFailed = false
    this._releaseLoadedGeometry()
    this._setResourceState(finalState)
    return { ...metrics, released, previousState }
  }

  private _releaseLoadedGeometry(): void {
    if (this.geometry) {
      this.geometry.dispose()
      this.geometry = null
    }
    for (let i = 0; i < this.oneTimeDisposeHandlers.length; i++) {
      const handler = this.oneTimeDisposeHandlers[i]
      handler()
    }
    this.oneTimeDisposeHandlers = []
  }

  private _setResourceState(state: NodeResourceState): void {
    if (this._resourceState === state) {
      return
    }
    const previousState = this._resourceState
    this._resourceState = state
    this.octreeGeometry.notifyResourceStateChanged(this, previousState, state)
  }

  private _emptyReleaseRecord(previousState: NodeResourceState): NodeReleaseRecord {
    return { ...EMPTY_RESOURCE_METRICS, released: false, previousState }
  }
}
